import React, { useMemo, useState } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import ToggleButton from 'components/uiLibrary/ToggleButton';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { useTrackingClickStyles } from 'components/Globals/TrackingTester';

import { TP } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';

import { useTranslation } from 'src/i18n';

import classes from './SortOptions.module.scss';

const SortOptions = ({ value, options, onSelect, styles, trackingData = {}, variant }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const trackingClasses = useTrackingClickStyles(trackingData, false);
  const { isMobile } = useDeviceTypeLayouts();
  const [isOpen, setIsOpen] = useState(false);

  const onChangeHandler = optionSelected => {
    track.click({
      ...trackingData,
      meta: {
        id: optionSelected?.value,
      },
    });

    if (onSelect) {
      onSelect(optionSelected?.value);
    }
  };

  const sortOptions = useMemo(
    () =>
      options?.map(option => ({
        ...option,
        label:
          typeof option?.label === 'string'
            ? `${t(option?.label, { ...(option?.labelProp && option?.labelProp) })} ${
                option?.suffix ? `(${t(option?.suffix)})` : ''
              }`
            : option?.label,
        icon: <SpriteIcon icon={option?.icon || 'swap'} size="14" className={classes.icon} />,
      })),
    [options, t],
  );

  const onOpen = isExpanded => {
    setIsOpen(isExpanded);
  };

  const selectedOption = useMemo(() => sortOptions?.find(option => option?.value === value), [sortOptions, value]);

  const label = useMemo(() => {
    if (variant === 'NEW') {
      return (
        <>
          <Typography size="12">{`${t(`${TP}.FN_SORTBY`)}: `}</Typography>
          {selectedOption?.label}
        </>
      );
    }

    return isOpen && !isMobile ? t(`${TP}.FN_SORTBY`) : selectedOption?.label;
  }, [variant, selectedOption?.label, t, isOpen, isMobile]);

  return (
    <ToggleButton
      title={isOpen ? t(`${TP}.FN_SORTBY`) : selectedOption?.label}
      options={sortOptions}
      label={label}
      leftIcon={selectedOption?.icon}
      rightIcon={<SpriteIcon icon="expand_more" size={14} className={classes.rightIcon} />}
      value={selectedOption?.value}
      onChange={onChangeHandler}
      onOpen={onOpen}
      styles={{
        radioBtn: classnames(classes.radioBtn, trackingClasses),
        selectedOption: classnames(classes.selectedOption, {
          [classes.selectedOption__new]: variant === 'NEW',
          [styles?.selectedOption]: !!styles?.selectedOption,
          [classes.collapsed]: isMobile || !isOpen,
        }),
        ...styles,
      }}
      alwaysDropdown
    />
  );
};

export default SortOptions;
